import { IClient } from '../../../../Assembler/dist/registry/iface/iClient.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { ScenariosFilter } from '../../registryCommon/dynamicData/scenariosFilter.js';

/**
 * Client-side CLIENT HANDLER for the scenario picker — the "processor" half of the two-handler split.
 * Consumes the RAW enriched scenario set (produced by ScenarioClientData, the server "preparer") and
 * filters/pages it into the final client-side result WITHOUT a server round-trip.
 *
 * - Parse the raw enriched scenario set handed IN (never enumerated/fetched here — source-agnostic)
 * - Apply the client's SelectedAppSiteFolder selection (resolved configJson > componentJson > parent),
 *   REUSING the shared ScenariosFilter so the client tier is byte-identical to the server processData
 *
 * Distinct from ScenarioClientData (the server preparer): this is the client processor, registered on the
 * SEPARATE IClient registry. The same body runs in the browser bundle and in the console
 * --apiclient/--ociclient simulation, certifying the browser artifact by construction.
 */
export class ScenarioClient extends IClient {
    /**
     * Gets the full type name for client-handler operation tracking.
     */
    get typeName(): string {
        return 'ScenarioClient';
    }

    /**
     * Filters the RAW enriched scenario set by ONLY the client's live SelectedAppSiteFolder selection,
     * REUSING the shared ScenariosData filter so the client tier is byte-identical to the server processData.
     * The STATIC skips (SkipViewAppSitesFolder / SkipViewAppSites) are already applied by the server preparer
     * ScenarioClientData, so the raw handed IN is skip-filtered — the browser (which cannot read server param
     * files) needs no skip params. Source-agnostic — the raw is handed in (embedded data island / remote slice).
     */
    process(appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, transientStore: ITransientStore, configJson: JsonObject | null, getParentJsonValue: GetParentJsonValueHandler | null, rawJson: JsonObject): JsonObject {
        void appSite; void appView; void appFile; void componentName; void componentDepth; void resolvedAppSite; void resolvedComponentName; void paramJson; void transientStore;
        if (rawJson === null || rawJson === undefined || !rawJson.has('Scenarios') || !rawJson.get('Scenarios').isArray) {
            return new JsonObject();
        }

        const scenarios = ScenariosFilter.parseEnrichedScenarios(rawJson.get('Scenarios').asArray());
        if (scenarios.length === 0) {
            return new JsonObject();
        }

        // Resolve SelectedAppSiteFolder IDENTICALLY to the server ScenariosData.processData:
        // configJson > componentJson > parent via delegate.
        let selectedFolder = '';
        if (configJson !== null && configJson.has('SelectedAppSiteFolder') && configJson.get('SelectedAppSiteFolder').isString) {
            selectedFolder = configJson.get('SelectedAppSiteFolder').toString();
        } else if (componentJson !== null && componentJson !== undefined && componentJson.has('SelectedAppSiteFolder') && componentJson.get('SelectedAppSiteFolder').isString) {
            selectedFolder = componentJson.get('SelectedAppSiteFolder').toString();
        } else if (getParentJsonValue !== null) {
            selectedFolder = getParentJsonValue('SelectedAppSiteFolder') ?? '';
        }

        // Folder filter ONLY (skips already baked into the raw by the preparer). Empty skip args => no-op skip.
        const filtered = ScenariosFilter.filterScenarios(scenarios, selectedFolder, '', '');
        return ScenariosFilter.buildScenariosResult(filtered, selectedFolder);
    }
}
